/**
 * Lab Brief — printable one-page workshop artefact.
 *
 * Route: /brief/:token (reuses the snapshot token for read-only access).
 *
 * The page is print-friendly out of the box: `@media print` rules in
 * index.css hide site chrome and interactive controls. Users click
 * "Print" in the page header (or browser print) to produce an A4 PDF.
 *
 * Build-time gated: in cierge builds, the entire RBT-laden brief is
 * replaced by a stub so Terser DCEs the RBT footer, RBT prompts heading,
 * and the "Right Brain Thinking" attribution.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getSnapshot, getScenarioBriefData } from "../lib/api";
import { Printer } from "lucide-react";

const NOT_CERT_NOTE =
    "Workshop artefact — not a certification or compliance attestation.";
const SECTOR_NOTE =
    "Sector presets adapt the learning language. The KATLAS governance proof model remains unchanged.";

function short(h) {
    if (!h) return "—";
    if (h.length <= 22) return h;
    return `${h.slice(0, 16)}…${h.slice(-6)}`;
}

function receiptMode(receipt) {
    if (receipt?.live) return "live · signed";
    if (receipt?.mode === "simulation") return "synthetic";
    return "unsigned · pending key";
}

function CiergeBriefStub() {
    return (
        <div
            className="max-w-3xl mx-auto px-6 lg:px-10 py-24 text-slate-500"
            data-testid="brief-unavailable"
        >
            Printable lab brief is not available in this deployment.
        </div>
    );
}

function RbtBrief() {
    const { token } = useParams();
    const [run, setRun] = useState(null);
    const [scenario, setScenario] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        getSnapshot(token)
            .then(async (r) => {
                setRun(r);
                const sectorId = r.sector?.id;
                const d = await getScenarioBriefData(r.scenario_slug, sectorId);
                setScenario(d.scenario);
            })
            .catch(() => setErr("Lab Brief not found"));
    }, [token]);

    if (err)
        return (
            <div className="max-w-3xl mx-auto px-6 py-24 text-slate-500" data-testid="brief-error">
                {err}
            </div>
        );
    if (!run || !scenario)
        return (
            <div className="max-w-3xl mx-auto px-6 py-24 text-slate-500">
                Loading Lab Brief…
            </div>
        );

    const ep = run.evidence_pack || {};
    const rec = run.receipt || {};
    const sector = run.sector || scenario.sector;
    const diagnostic = run.diagnostic || {};

    return (
        <div className="brief-print bg-white" data-testid="brief-page">
            <div className="max-w-5xl mx-auto px-6 py-10 print:py-0">
                {/* Brief header + Print action (hidden in print) */}
                <BriefHeader scenario={scenario} sector={sector} />

                {/* Scenario summary */}
                <Section title="Scenario">
                    <h2 className="text-2xl font-bold tracking-tight mb-1">
                        {scenario.title}
                    </h2>
                    <p className="text-sm text-slate-700 leading-relaxed">
                        {scenario.summary}
                    </p>
                    {scenario.use_case && (
                        <p className="mt-2 text-sm italic text-slate-600">
                            {scenario.use_case}
                        </p>
                    )}
                </Section>

                {/* Decision + sector pair */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Section title="Human decision">
                        <p className="text-base font-semibold text-slate-900">
                            {run.decision?.decision_label}
                        </p>
                        <p className="text-sm text-slate-600 mt-1">
                            Authorised by{" "}
                            <strong>{run.decision?.accountable_role}</strong>.
                        </p>
                    </Section>
                    <Section title="Sector preset">
                        <p className="text-base font-semibold text-slate-900">
                            {sector?.label || "Generic Executive Lab"}
                        </p>
                        <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500 mt-1">
                            {sector?.note || "neutral, sector-agnostic"}
                        </p>
                    </Section>
                </div>

                {/* Roles + Tenets */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Section title="Roles involved">
                        <ol className="space-y-1 text-sm text-slate-800">
                            {scenario.roles.map((r, i) => (
                                <li key={r} className="flex gap-2">
                                    <span className="font-mono text-[11px] text-slate-500 w-6">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span>{r}</span>
                                </li>
                            ))}
                        </ol>
                    </Section>
                    <Section title="RBT baseline tenets touched">
                        <ul className="space-y-1 text-sm text-slate-800">
                            {(scenario.tenets || []).map((t) => (
                                <li key={t.title} className="flex gap-2">
                                    <span className="font-mono text-[11px] text-slate-500 w-6">
                                        {t.n}
                                    </span>
                                    <span>{t.title}</span>
                                </li>
                            ))}
                        </ul>
                    </Section>
                </div>

                {/* CAR */}
                <Section title="CAR mapping">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-slate-800">
                        <CarBlock label="Custody" value={scenario.car_mapping.custody} />
                        <CarBlock label="Authority" value={scenario.car_mapping.authority} />
                        <CarBlock label="Receipts" value={scenario.car_mapping.receipts} />
                    </div>
                </Section>

                {/* AI advisory note */}
                <Section title="AI advisory note">
                    <p className="text-sm text-slate-700 leading-relaxed">
                        AI advisories surfaced inside this scenario are advisory only.
                        The named human role retains accountable authority. AI suggestions
                        did not, and could not, override the human decision gate.
                    </p>
                </Section>

                {/* Evidence shared / withheld / not sent */}
                <Section title="Evidence boundary">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                        <EvidenceList
                            label="Shared (references only)"
                            tone="ok"
                            items={ep.shared_to_katlas?.shared_evidence_refs || rec.shared_evidence_refs}
                        />
                        <EvidenceList
                            label="Withheld"
                            tone="warn"
                            items={ep.withheld_from_katlas || rec.withheld_evidence}
                        />
                        <EvidenceList
                            label="Not sent to KATLAS node"
                            tone="muted"
                            italic
                            items={ep.not_sent_to_node || rec.not_sent_to_node}
                        />
                    </div>
                </Section>

                {/* KATLAS proof card summary */}
                <Section title="KATLAS proof receipt">
                    <div className="terminal rounded-sm p-4">
                        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 font-mono text-sm">
                            <Row label="Mode">
                                <span className={rec.live ? "ok" : "warn"}>
                                    {receiptMode(rec)}
                                </span>
                            </Row>
                            <Row label="Verification status">
                                {rec.verification_status || "—"}
                            </Row>
                            <Row label="Receipt hash">
                                <span data-testid="brief-receipt-hash">{short(rec.receipt_hash)}</span>
                            </Row>
                            <Row label="Payload hash">{short(rec.payload_hash)}</Row>
                            <Row label="Node timestamp">{rec.node_timestamp || "—"}</Row>
                            <Row label="Signer">{rec.signer || "—"}</Row>
                            <Row label="Attestation">{rec.attestation_ref || "—"}</Row>
                            <Row label="Scenario ID">{rec.scenario_id || "—"}</Row>
                            <Row label="Decision ID">{rec.decision_id || "—"}</Row>
                        </dl>
                    </div>
                </Section>

                {/* Readiness diagnostic summary */}
                <Section title="Readiness diagnostic (workshop)">
                    <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-amber-700 mb-2">
                        Workshop diagnostic — not a certification
                    </p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-sm text-slate-800">
                        {Object.entries(diagnostic).map(([k, v]) => (
                            <li
                                key={k}
                                className="flex items-center justify-between border-b border-slate-100 py-1"
                            >
                                <span>{k.replace(/_/g, " ")}</span>
                                <span className="font-mono">{v} / 5</span>
                            </li>
                        ))}
                    </ul>
                </Section>

                {/* Workshop follow-up prompts */}
                <Section title="RBT workshop follow-up prompts">
                    <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm text-slate-800 list-decimal pl-5">
                        {(scenario.rbt_prompts || []).map((p) => (
                            <li key={p} className="leading-relaxed">
                                {p}
                            </li>
                        ))}
                    </ol>
                </Section>

                {/* Footer notes */}
                <div className="mt-8 pt-4 border-t border-slate-300 text-[11px] font-mono uppercase tracking-[0.18em] text-slate-600 space-y-1">
                    <p data-testid="brief-not-cert-note">{NOT_CERT_NOTE}</p>
                    <p>{SECTOR_NOTE}</p>
                    <p className="normal-case tracking-normal text-slate-500 font-sans">
                        Right Brain Thinking International × KATLAS Technology · Responsible Technology Leadership Lab
                    </p>
                </div>
            </div>
        </div>
    );
}

const Brief = process.env.REACT_APP_BRAND_MODE === "cierge"
    ? CiergeBriefStub
    : RbtBrief;

export default Brief;

function BriefHeader({ scenario, sector }) {
    return (
        <header className="flex items-baseline justify-between gap-4 mb-6">
            <div>
                <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-slate-500">
                    Lab Brief · RBT × KATLAS · {sector?.short_label || "Generic"}
                </div>
                <h1 className="text-3xl font-black tracking-tight mt-1">
                    {scenario.title}
                </h1>
            </div>
            <button
                onClick={() => window.print()}
                data-print-hide="true"
                data-testid="brief-print-btn"
                className="btn-primary text-sm tracking-tight px-4 py-2 rounded-sm inline-flex items-center gap-2"
            >
                <Printer size={14} strokeWidth={1.5} /> Print
            </button>
        </header>
    );
}

function Section({ title, children }) {
    return (
        <section className="mt-5 first:mt-0">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2 border-b border-slate-200 pb-1">
                {title}
            </div>
            {children}
        </section>
    );
}

function Row({ label, children }) {
    return (
        <div className="flex items-center justify-between gap-3 border-b border-slate-800/60 pb-1">
            <dt className="text-slate-400 uppercase tracking-[0.18em] text-[10px]">
                {label}
            </dt>
            <dd className="text-slate-100 truncate text-right">{children}</dd>
        </div>
    );
}

function CarBlock({ label, value }) {
    return (
        <div>
            <div className="overline mb-1">{label}</div>
            <p className="leading-relaxed">{value}</p>
        </div>
    );
}

function EvidenceList({ label, items, tone, italic }) {
    const colors = {
        ok: "text-emerald-700",
        warn: "text-amber-700",
        muted: "text-slate-500",
    };
    return (
        <div>
            <div className={`overline mb-2 ${colors[tone] || ""}`}>{label}</div>
            <ul className={`space-y-1 text-slate-700 ${italic ? "italic" : ""}`}>
                {(items || []).map((it) => (
                    <li key={it} className="flex gap-2">
                        <span className="text-slate-400">·</span>
                        <span>{it}</span>
                    </li>
                ))}
                {(!items || items.length === 0) && (
                    <li className="text-slate-400 italic">—</li>
                )}
            </ul>
        </div>
    );
}
